import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import RsvpButton from '../components/RsvpButton';

type ClubSummary = {
  id: string;
  slug: string;
  name: string;
  location?: string | null;
  logo?: string | null;
  hero_image?: string | null;
  instagram_handle?: string | null;
};

type RouteSummary = {
  id: string;
  name: string;
  description?: string | null;
  route_type?: string | null;
  start_location?: string | null;
  distance_miles?: number | null;
  elevation_gain_ft?: number | null;
  surface?: string | null;
  external_url?: string | null;
  gpx_file_url?: string | null;
  is_public?: boolean | null;
};

type RunRow = {
  id: string;
  club_id: string;
  title?: string | null;
  day_of_week: string;
  start_time: string;
  location_address?: string | null;
  distance?: string | null;
  vibes?: string[] | null;
  notes?: string | null;
  route_id?: string | null;
  route_url?: string | null;
  club?: ClubSummary | ClubSummary[] | null;
  route?: RouteSummary | RouteSummary[] | null;
};

// An exception to a standing weekly slot on one date. No row = the slot runs as
// written on `runs`.
type RunInstanceRow = {
  id: string;
  run_id: string;
  club_id: string;
  run_date: string;
  status: 'canceled' | 'moved' | 'special';
  title?: string | null;
  start_time_override?: string | null;
  location_address_override?: string | null;
  distance_override?: string | null;
  notes_override?: string | null;
  route_id?: string | null;
  route?: RouteSummary | RouteSummary[] | null;
};

type EffectiveRun = {
  id: string;
  club_id: string;
  club: ClubSummary | null;
  title?: string | null;
  day_of_week: string;
  start_time: string;
  location_address?: string | null;
  distance?: string | null;
  vibes: string[];
  notes?: string | null;
  route: RouteSummary | null;
  route_url?: string | null;
  // 'scheduled' = the standing slot as written; otherwise the exception's status.
  status: 'scheduled' | 'moved' | 'special';
  isOverride: boolean;
};

function getTodayDayName(date: Date) {
  return new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(date);
}

function getIsoDate(date: Date) {
  // Local calendar date — toISOString() is UTC and rolls over at 4/5pm Pacific.
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDisplayDate(date: Date) {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(date);
}

function formatTime(time?: string | null) {
  if (!time) return 'Time TBD';

  const trimmed = time.trim();
  if (/am|pm/i.test(trimmed)) return trimmed;

  const [rawHour, rawMinute] = trimmed.split(':');
  const hour = Number(rawHour);
  const minute = rawMinute ?? '00';

  if (Number.isNaN(hour)) return trimmed;

  const normalizedHour = hour % 12 || 12;
  const suffix = hour >= 12 ? 'PM' : 'AM';

  return `${normalizedHour}:${minute} ${suffix}`;
}

function sortByTime(a: EffectiveRun, b: EffectiveRun) {
  return a.start_time.localeCompare(b.start_time);
}

function firstLineAddress(address?: string | null) {
  if (!address) return 'Location TBD';
  return address.split(',')[0]?.trim() || address;
}

function normalizeClub(value?: ClubSummary | ClubSummary[] | null): ClubSummary | null {
  if (!value) return null;
  return Array.isArray(value) ? value[0] ?? null : value;
}

function normalizeRoute(
  value?: RouteSummary | RouteSummary[] | null
): RouteSummary | null {
  if (!value) return null;
  return Array.isArray(value) ? value[0] ?? null : value;
}

function isEffectiveRun(run: EffectiveRun | null): run is EffectiveRun {
  return run !== null;
}

function getStatusLabel(run: EffectiveRun) {
  if (run.status === 'moved') return 'Changed this week';
  if (run.status === 'special') return 'Special run';
  return 'Weekly run';
}

export default function TodayRuns() {
  const [runs, setRuns] = useState<EffectiveRun[]>([]);
  const [loading, setLoading] = useState(true);

  const now = useMemo(() => new Date(), []);
  const dayName = useMemo(() => getTodayDayName(now), [now]);
  const iso = useMemo(() => getIsoDate(now), [now]);

  useEffect(() => {
    async function fetchTodayRuns() {
      setLoading(true);

      const runSelectFields = `
        id,
        club_id,
        title,
        day_of_week,
        start_time,
        location_address,
        distance,
        vibes,
        notes,
        route_id,
        route_url,
        club:clubs (
          id,
          slug,
          name,
          location,
          logo,
          hero_image,
          instagram_handle
        ),
        route:routes!runs_route_id_fkey (
          id,
          name,
          description,
          route_type,
          start_location,
          distance_miles,
          elevation_gain_ft,
          surface,
          external_url,
          gpx_file_url,
          is_public
        )
      `;

      const [
        { data: recurringData, error: recurringError },
        { data: oneOffData, error: oneOffError },
        { data: instancesData, error: instancesError },
      ] = await Promise.all([
        // Recurring runs: match by day of week
        supabase
          .from('runs')
          .select(runSelectFields)
          .eq('day_of_week', dayName)
          .eq('is_recurring', true)
          .eq('is_active', true),

        // One-off runs: match by exact date
        supabase
          .from('runs')
          .select(runSelectFields)
          .eq('run_date', iso)
          .eq('is_recurring', false)
          .eq('is_active', true),

        supabase
          .from('run_instances')
          .select(`
            id,
            run_id,
            club_id,
            run_date,
            status,
            title,
            start_time_override,
            location_address_override,
            distance_override,
            notes_override,
            route_id,
            route:routes!run_instances_route_id_fkey (
              id,
              name,
              description,
              route_type,
              start_location,
              distance_miles,
              elevation_gain_ft,
              surface,
              external_url,
              gpx_file_url,
              is_public
            )
          `)
          .eq('run_date', iso),
      ]);

      const runsError = recurringError || oneOffError;
      let runsData: RunRow[] = [
        ...((recurringData || []) as RunRow[]),
        ...((oneOffData || []) as RunRow[]),
      ];

      // A slot moved onto today from another weekday shows up only as an
      // exception row, so pull its parent run in as well.
      const knownRunIds = new Set(runsData.map((run) => run.id));
      const movedInRunIds = ((instancesData || []) as RunInstanceRow[])
        .filter((instance) => instance.status !== 'canceled' && !knownRunIds.has(instance.run_id))
        .map((instance) => instance.run_id);

      if (movedInRunIds.length > 0) {
        const { data: movedInData } = await supabase
          .from('runs')
          .select(runSelectFields)
          .in('id', movedInRunIds)
          .eq('is_active', true);
        runsData = [...runsData, ...((movedInData || []) as RunRow[])];
      }

      if (runsError) {
        console.error('Error fetching today runs:', (runsError as { message?: string })?.message);
        setRuns([]);
        setLoading(false);
        return;
      }

      if (instancesError) {
        console.error('Error fetching run instances:', instancesError.message);
      }

      const instanceMap = new Map<string, RunInstanceRow>();
      ((instancesData || []) as RunInstanceRow[]).forEach((instance) => {
        instanceMap.set(instance.run_id, instance);
      });

      const mappedRuns: Array<EffectiveRun | null> = ((runsData || []) as RunRow[]).map(
        (run) => {
          const instance = instanceMap.get(run.id);
          const baseRoute = normalizeRoute(run.route);
          const overrideRoute = normalizeRoute(instance?.route);
          if (instance?.status === 'canceled') {
            return null;
          }

          const status: EffectiveRun['status'] = instance?.status ?? 'scheduled';

          return {
            id: run.id,
            club_id: run.club_id,
            club: normalizeClub(run.club),
            title: instance?.title ?? run.title ?? null,
            day_of_week: run.day_of_week,
            start_time: instance?.start_time_override || run.start_time,
            location_address:
              instance?.location_address_override || run.location_address || null,
            distance: instance?.distance_override || run.distance || null,
            vibes: run.vibes || [],
            notes: instance?.notes_override || run.notes || null,
            route: overrideRoute || baseRoute,
            route_url: run.route_url || null,
            status,
            isOverride: !!instance,
          };
        }
      );

      const effectiveRuns = mappedRuns.filter(isEffectiveRun).sort(sortByTime);

      setRuns(effectiveRuns);
      setLoading(false);
    }

    fetchTodayRuns();
  }, [dayName, iso]);

  const headerCopy = useMemo(() => {
    return `Runs happening ${formatDisplayDate(now)}`;
  }, [now]);

  return (
    <main className="min-h-screen bg-gradient-to-br from-[#fffdf3] via-[#fcfcfb] to-[#faf2fd] pb-16">
      <div className="mx-auto max-w-6xl px-6 pt-6">
        <div className="overflow-hidden rounded-[2rem] border border-black/10 bg-white shadow-[0_18px_60px_rgba(0,0,0,0.08)]">
          <div className="bg-gradient-to-r from-[#fff7cc] via-white to-[#f6e8fb] px-8 py-10 text-black md:px-10 md:py-12">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-black/45">
              Today
            </p>

            <h1 className="mt-3 text-4xl font-semibold tracking-tight text-black md:text-5xl">
              {headerCopy}
            </h1>

            <p className="mt-3 max-w-2xl text-sm leading-6 text-black/65 sm:text-base">
              Today’s recurring club runs, all in one place.
            </p>
          </div>
        </div>

        <div className="mt-8">
          {loading ? (
            <div className="grid gap-6 md:grid-cols-2">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="h-72 animate-pulse rounded-3xl border border-black/10 bg-gradient-to-r from-[#fffdf2] via-white to-[#fbf4fe] shadow-sm"
                />
              ))}
            </div>
          ) : runs.length === 0 ? (
            <div className="rounded-3xl border border-black/10 bg-gradient-to-r from-[#fff7cc] via-white to-[#f6e8fb] p-8 shadow-sm">
              <h2 className="text-2xl font-semibold text-black">No runs today</h2>
              <p className="mt-3 text-black/60">
                Nothing is scheduled for today right now.
              </p>
              <div className="mt-6">
                <Link
                  to="/clubs"
                  className="inline-flex rounded-2xl bg-black px-5 py-3 text-sm font-medium text-white transition hover:opacity-90"
                >
                  Explore Clubs
                </Link>
              </div>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2">
              {runs.map((run) => {
                const routeHref = run.route?.external_url || run.route_url || null;

                return (
                  <div
                    key={run.id}
                    className="overflow-hidden rounded-3xl border border-black/10 bg-gradient-to-r from-[#fffdf2] via-white to-[#fbf4fe] shadow-sm"
                  >
                    <div className="relative h-48 bg-neutral-100">
                      {run.club?.hero_image ? (
                        <img
                          src={run.club.hero_image}
                          alt={run.club.name}
                          className="h-full w-full object-cover"
                          style={{ objectPosition: 'center 33%' }}
                        />
                      ) : (
                        <div className="h-full w-full bg-gradient-to-br from-[#fff7cc] via-white to-[#f6e8fb]" />
                      )}

                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-transparent" />

                      <div className="absolute bottom-0 left-0 right-0 p-5">
                        <div className="flex items-end justify-between gap-4">
                          <div>
                            <p className="text-xs uppercase tracking-[0.14em] text-white/75">
                              {getStatusLabel(run)}
                            </p>

                            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-white">
                              {run.title || run.club?.name || 'Run'}
                            </h2>

                            <p className="mt-1 text-sm text-white/80">
                              {firstLineAddress(run.location_address || run.club?.location)}
                            </p>
                          </div>

                          {run.club?.logo ? (
                            <div className="h-14 w-14 overflow-hidden rounded-2xl border border-white/50 bg-white shadow-lg">
                              <img
                                src={run.club.logo}
                                alt={run.club.name}
                                className="h-full w-full object-cover"
                              />
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4 p-5">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="rounded-full border border-black/10 bg-white px-3 py-1.5 text-sm font-semibold text-black">
                          {formatTime(run.start_time)}
                        </span>

                        {run.distance ? (
                          <span className="rounded-full border border-black/10 bg-white px-3 py-1.5 text-sm font-medium text-black/70">
                            {run.distance}
                          </span>
                        ) : null}

                        {run.isOverride ? (
                          <span className="rounded-full border border-black/10 bg-[#fff7cc] px-3 py-1.5 text-sm font-medium text-black">
                            Updated
                          </span>
                        ) : null}
                      </div>

                      {run.vibes.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {run.vibes.slice(0, 4).map((vibe) => (
                            <span
                              key={vibe}
                              className="rounded-full border border-black/10 bg-white px-3 py-1 text-xs font-medium text-black/65"
                            >
                              {vibe}
                            </span>
                          ))}
                        </div>
                      ) : null}

                      {run.notes ? (
                        <p className="text-sm leading-6 text-black/65">{run.notes}</p>
                      ) : null}

                      <div className="space-y-2">
                        {/* RSVP row — full width so buttons never compete for space */}
                        <RsvpButton
                          runId={run.id}
                          runDate={iso}
                          clubId={run.club?.slug ?? run.club_id}
                          fullWidth
                        />

                        {/* Navigation CTAs on their own row */}
                        {(run.club || routeHref) && (
                          <div className="flex flex-wrap gap-2">
                            {run.club && (
                              <Link
                                to={`/clubs/${run.club.slug}`}
                                className="inline-flex rounded-2xl border border-black/10 bg-white px-4 py-2.5 text-sm font-medium text-black transition hover:bg-black hover:text-white"
                              >
                                View Club
                              </Link>
                            )}
                            {routeHref && (
                              <a
                                href={routeHref}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex rounded-2xl border border-black/10 bg-white px-4 py-2.5 text-sm font-medium text-black transition hover:bg-black hover:text-white"
                              >
                                View Route ↗
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}